import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { getCurrentInstance } from 'vue'

export interface ConnectionState {
  pingInterval: NodeJS.Timeout | null
  timeoutSeconds: number
  lastPing: Date | null
  reconnectTimeout: NodeJS.Timeout | null
}

export function useConnection() {
  const store = useStore()
  const instance = getCurrentInstance()

  // State
  const state = ref<ConnectionState>({
    pingInterval: null,
    timeoutSeconds: 55,
    lastPing: null,
    reconnectTimeout: null,
  })

  // Computed properties
  const isConnected = computed(() => store.state.connection.wsConnected)
  const isLoaded = computed(() => store.state.connection.wsLoaded)
  const wsSession = computed(() => store.state.connection.wsSession)
  const lastTimeBuild = computed(() => store.state.connection.lastTimeBuild)

  // WebSocket methods
  const wsSend = (action: string, params?: any) => {
    if (instance?.proxy) {
      (instance.proxy as any).$wsSend(action, params)
    }
  }

  const wsReConnect = () => {
    if (instance?.proxy) {
      (instance.proxy as any).$wsReConnect()
    }
  }

  // Ping management
  const startPing = () => {
    // Очищаем предыдущий интервал если есть
    if (state.value.pingInterval) {
      clearInterval(state.value.pingInterval)
    }
    
    // Обновляем lastPing
    state.value.lastPing = new Date()
    
    // Запускаем ping каждые timeoutSeconds секунд
    state.value.pingInterval = setInterval(() => {
      wsSend('ping', {})
      state.value.lastPing = new Date()
    }, 1000 * state.value.timeoutSeconds)
  }

  const stopPing = () => {
    if (state.value.pingInterval) {
      clearInterval(state.value.pingInterval)
      state.value.pingInterval = null
    }
  }

  // Connection event handlers
  const onOpen = () => {
    store.dispatch('connection/setWsConnected', true)
    store.dispatch('connection/initSession')

    wsSend('authSession', {
      session: wsSession.value,
    })
    
    startPing()
  }

  const onClose = (event: CloseEvent) => {
    stopPing()
    store.dispatch('connection/setWsConnected', false)
    
    console.log('Socket is closed. Reconnect will be attempted in 3 seconds.', event.reason)

    // Close all open modals and toasts on disconnect to avoid stale UI
    try {
      // Close modals by triggering their built-in close handlers
      const openModals = document.querySelectorAll('.modal.show')
      openModals.forEach((modalEl) => {
        const closeBtn = (modalEl as HTMLElement).querySelector('.btn-close') as HTMLElement | null
        if (closeBtn) {
          ;(closeBtn as HTMLButtonElement).click()
        } else {
          // Fallback: send Escape which BaseModal listens for
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
        }
      })
      // Ensure body scroll lock is removed
      document.body.classList.remove('modal-open')

      // Remove any visible toasts
      const toastContainer = document.getElementById('toastContainer')
      if (toastContainer) {
        const toasts = toastContainer.querySelectorAll('.toast')
        toasts.forEach((toast) => (toast as HTMLElement).remove())
      }
    } catch (e) {
      // Non-fatal: UI cleanup best-effort
      console.warn('UI cleanup on WS disconnect failed:', e)
    }
    
    // Защита от частых переподключений
    if (state.value.reconnectTimeout) {
      clearTimeout(state.value.reconnectTimeout)
    }
    
    state.value.reconnectTimeout = setTimeout(() => {
      if (isConnected.value) {
        console.warn('WebSocket is already connected. Skip reconnecting.')
        state.value.reconnectTimeout = null
        return
      }
      wsReConnect()
      state.value.reconnectTimeout = null
    }, 3000)
  }

  const onError = (error: Event) => {
    console.error('WebSocket error', error)
  }

  const onMessage = (event: MessageEvent) => {
    let json = JSON.parse(event.data)
    let signal = json.signal
    delete json.signal

    if (signal !== 'pong') {
      console.log('wsMessageHandler', signal, json);
    }
  }

  // Lifecycle methods
  const setupConnection = () => {
    if (instance?.proxy) {
      (instance.proxy as any).$wsSetOnOpen(onOpen)
      ;(instance.proxy as any).$wsSetOnClose(onClose)
      ;(instance.proxy as any).$wsSetOnError(onError)
      ;(instance.proxy as any).$wsSetOnMessage(onMessage)
      ;(instance.proxy as any).$wsReConnect()
    }
  }

  const cleanup = () => {
    stopPing()
    if (state.value.reconnectTimeout) {
      clearTimeout(state.value.reconnectTimeout)
      state.value.reconnectTimeout = null
    }
  }

  return {
    // State
    timeoutSeconds: computed(() => state.value.timeoutSeconds),
    lastPing: computed({
      get: () => state.value.lastPing,
      set: (value: Date | null) => { state.value.lastPing = value }
    }),
    
    // Computed
    isConnected,
    isLoaded,
    wsSession,
    lastTimeBuild,
    
    // Methods
    wsSend,
    wsReConnect,
    startPing,
    stopPing,
    setupConnection,
    cleanup,
  }
} 